import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Progress } from '../components/ui/progress';
import {
  Heart, MessageCircle, Trophy, Award,
  Star, ChevronLeft, BarChart3, ArrowRight,
  Gift, Filter, PieChart as PieIcon, PenLine, Trash2, Loader2, Settings
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  LEVELS, getLevel, getNextLevel, TAGS, BADGES
} from '../data/communityData';
import ThesisFormModal from './community/ThesisFormModal';
import ThesisComments from './community/ThesisComments';
import { communityApi } from './community/communityApi';
import FeedTab from './community/FeedTab';
import QATab from './community/QATab';
import ResultsTab from './community/ResultsTab';
import SharingSettingsModal from './community/SharingSettingsModal';
import NotificationsPanel from './community/NotificationsPanel';

const fmtCur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const ALLOC_COLORS = { ETF: '#00e5a0', Actions: '#4f8eff', Crypto: '#7c3aed', Immobilier: '#f97316', Épargne: '#22d3ee', Autres: '#94a3b8' };
const TAB_LIST = ['Fil', 'Thèses', 'Q&A', 'Résultats', 'Portefeuilles', 'Tendances', 'Classement', 'Badges'];

// ── Stars rating ──
const Stars = ({ rating, count, userRating, onRate }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map(s => (
      <Star key={s} className={`w-3.5 h-3.5 cursor-pointer transition-colors ${s <= Math.round(userRating || rating) ? 'text-amber-400 fill-amber-400' : 'text-white/15 hover:text-amber-400/50'}`}
        onClick={(e) => { e.stopPropagation(); onRate?.(s); }} />
    ))}
    {count > 0 && <span className="text-cap-text-muted text-[10px] ml-1">({count})</span>}
  </div>
);

// ── Avatar ──
const MemberAvatar = ({ username, size = 'md', level }) => {
  const s = size === 'lg' ? 'w-14 h-14 text-lg' : size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  const colors = ['from-emerald-500/40 to-emerald-900/40', 'from-blue-500/40 to-blue-900/40', 'from-purple-500/40 to-purple-900/40', 'from-amber-500/40 to-amber-900/40', 'from-red-500/40 to-red-900/40', 'from-cyan-500/40 to-cyan-900/40'];
  const idx = (username || '').charCodeAt(0) % colors.length;
  return (
    <div className={`${s} rounded-full bg-gradient-to-br ${colors[idx]} flex items-center justify-center font-display font-bold text-white border border-white/10 relative flex-shrink-0`}>
      {(username || '?')[0]}
      {level && <span className="absolute -bottom-0.5 -right-0.5 text-[8px] bg-[#141720] border border-white/10 rounded-full w-4 h-4 flex items-center justify-center font-mono text-[#00e5a0]">{level}</span>}
    </div>
  );
};

// ── Badge pill ──
const BadgePill = ({ badgeId }) => {
  const b = BADGES.find(x => x.id === badgeId);
  if (!b) return null;
  return <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-cap-text-secondary">{b.icon} {b.name}</span>;
};

// ── Render formatted text ──
const renderFormattedText = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, li) => {
    const isList = line.startsWith('- ');
    const content = isList ? line.slice(2) : line;
    const parts = [];
    let key = 0;
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let lastIdx = 0;
    let match;
    while ((match = regex.exec(content)) !== null) {
      if (match.index > lastIdx) parts.push(<span key={key++}>{content.slice(lastIdx, match.index)}</span>);
      if (match[2]) parts.push(<strong key={key++} className="font-bold text-white">{match[2]}</strong>);
      else if (match[3]) parts.push(<em key={key++} className="italic text-cap-text-secondary">{match[3]}</em>);
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < content.length) parts.push(<span key={key++}>{content.slice(lastIdx)}</span>);
    if (parts.length === 0) parts.push(<span key={0}>{content}</span>);
    if (isList) return <li key={li} className="ml-4 list-disc text-cap-text-secondary text-sm">{parts}</li>;
    if (!content.trim()) return <br key={li} />;
    return <p key={li} className="text-cap-text-secondary text-sm">{parts}</p>;
  });
};

// ══════════════════════════════════════
// THÈSES TAB (API-backed, improved spacing)
// ══════════════════════════════════════
const ThesesTab = ({ onOpenForm, currentUsername, onXpChange }) => {
  const [filterTag, setFilterTag] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [theses, setTheses] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTheses = useCallback(async () => {
    try { setTheses(await communityApi.listTheses(filterTag)); }
    catch { /* */ } finally { setLoading(false); }
  }, [filterTag]);

  useEffect(() => { loadTheses(); }, [loadTheses]);

  const handleLike = async (id) => {
    try {
      const r = await communityApi.toggleLike(id);
      setTheses(prev => prev.map(t => t.id === id ? { ...t, likes_count: r.likes_count, user_liked: r.liked } : t));
    } catch { /* */ }
  };

  const handleRate = async (id, score) => {
    try {
      const r = await communityApi.rateThesis(id, score);
      setTheses(prev => prev.map(t => t.id === id ? { ...t, rating_avg: r.rating_avg, rating_count: r.rating_count, user_rating: r.user_rating } : t));
    } catch { /* */ }
  };

  const handleDelete = async (id) => {
    try { await communityApi.deleteThesis(id); setTheses(prev => prev.filter(t => t.id !== id)); toast.success('Thèse supprimée'); }
    catch { toast.error('Erreur'); }
  };

  const handleCommentCountChange = (id, delta) => {
    setTheses(prev => prev.map(t => t.id === id ? { ...t, comments_count: Math.max(0, (t.comments_count || 0) + delta) } : t));
    if (delta > 0) onXpChange?.(10);
  };

  return (
    <div data-testid="theses-tab">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        <Filter className="w-4 h-4 text-cap-text-muted" />
        <button onClick={() => setFilterTag(null)}
          className={`text-xs px-3 py-1.5 rounded-full transition-all ${!filterTag ? 'bg-[#00e5a0]/20 text-[#00e5a0] font-bold' : 'bg-white/5 text-cap-text-secondary hover:bg-white/10'}`}>
          Tout
        </button>
        {TAGS.map(t => (
          <button key={t} onClick={() => setFilterTag(filterTag === t ? null : t)}
            className={`text-xs px-3 py-1.5 rounded-full transition-all ${filterTag === t ? 'bg-[#00e5a0]/20 text-[#00e5a0] font-bold' : 'bg-white/5 text-cap-text-secondary hover:bg-white/10'}`}>
            #{t}
          </button>
        ))}
      </div>
      {/* Publish CTA */}
      <button onClick={onOpenForm} className="w-full bg-[#1a1d27] border border-white/5 rounded-xl p-4 flex items-center gap-3 hover:border-[#00e5a0]/20 transition-all group mb-6"
        data-testid="open-thesis-form-btn">
        <MemberAvatar username={currentUsername || 'Moi'} size="sm" />
        <div className="flex-1 text-left text-cap-text-muted text-sm group-hover:text-white transition-colors">Publier une thèse d'investissement...</div>
        <div className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-8 px-4 rounded-md flex items-center gap-1.5">
          <PenLine className="w-3.5 h-3.5" /> Publier
        </div>
      </button>
      {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cap-text-muted" /></div>}
      {!loading && theses.length === 0 && <p className="text-cap-text-muted text-sm text-center py-12">Aucune thèse. Soyez le premier !</p>}
      {/* Feed — improved spacing 32px gap + shadow */}
      <div className="flex flex-col gap-8">
        {theses.map((t, i) => {
          const isExpanded = expandedId === t.id;
          return (
            <motion.div key={t.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 5) * 0.04 }}
              className={`bg-[#1a1d27] border rounded-xl p-5 transition-all ${t.is_owner ? 'border-[#00e5a0]/15' : 'border-white/[0.06]'}`}
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
              data-testid={`thesis-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <MemberAvatar username={t.author} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium text-sm">{t.author}</p>
                      {t.is_owner && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#00e5a0]/10 text-[#00e5a0] font-bold">VOUS</span>}
                    </div>
                    <p className="text-cap-text-muted text-[10px]">{new Date(t.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#00e5a0] text-sm bg-[#00e5a0]/10 px-2.5 py-1 rounded-lg">${t.symbol}</span>
                  {t.is_owner && (
                    <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded-lg hover:bg-red-400/10 text-cap-text-muted hover:text-red-400 transition-colors"
                      data-testid={`delete-thesis-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
              <h3 className="font-display text-base font-semibold text-white mb-2">{t.title}</h3>
              {t.summary && <p className="text-cap-text-secondary text-sm mb-2 italic">{t.summary}</p>}
              {isExpanded ? <div className="mb-3 space-y-1">{renderFormattedText(t.content)}</div>
                : <p className="text-cap-text-secondary text-sm mb-3 line-clamp-2">{(t.content || '').replace(/\*\*/g, '').replace(/\*/g, '')}</p>}
              {(t.content || '').length > 150 && (
                <button onClick={() => setExpandedId(isExpanded ? null : t.id)} className="text-[#00e5a0] text-xs mb-3 hover:underline">{isExpanded ? 'Voir moins' : 'Lire la suite'}</button>
              )}
              <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                {(t.tags || []).map(tag => <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-cap-text-secondary cursor-pointer hover:bg-[#00e5a0]/10 hover:text-[#00e5a0]" onClick={() => setFilterTag(tag)}>#{tag}</span>)}
                {t.has_analysis && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#4f8eff]/10 text-[#4f8eff] font-medium flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Analyse jointe</span>}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <div className="flex items-center gap-5">
                  <button onClick={() => handleLike(t.id)}
                    className={`flex items-center gap-1.5 transition-colors text-xs ${t.user_liked ? 'text-red-400' : 'text-cap-text-secondary hover:text-red-400'}`}
                    data-testid={`like-${t.id}`}>
                    <Heart className={`w-4 h-4 ${t.user_liked ? 'fill-red-400' : ''}`} /> <span>{t.likes_count || 0}</span>
                  </button>
                  <ThesisComments thesisId={t.id} commentsCount={t.comments_count || 0} onCountChange={(d) => handleCommentCountChange(t.id, d)} />
                </div>
                <Stars rating={t.rating_avg || 0} count={t.rating_count || 0} userRating={t.user_rating} onRate={(s) => handleRate(t.id, s)} />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// ══════════════════════════════════════
// PORTEFEUILLES TAB (API-backed)
// ══════════════════════════════════════
const PortfoliosTab = () => {
  const [selected, setSelected] = useState(null);
  const [showSharing, setShowSharing] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    communityApi.getPublicPortfolios()
      .then(setMembers)
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, []);

  if (selected) {
    const m = selected;
    const lvl = getLevel(m.xp || 0);
    const allocData = [];
    const typeMap = {};
    (m.allPositions || []).forEach(p => { typeMap[p.type] = (typeMap[p.type] || 0) + p.value; });
    Object.entries(typeMap).forEach(([k, v]) => allocData.push({ name: k, value: v }));
    return (
      <div className="space-y-6" data-testid="portfolio-detail">
        <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-cap-text-secondary hover:text-white text-sm"><ChevronLeft className="w-4 h-4" /> Retour</button>
        <div className="bg-[#1a1d27] border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <MemberAvatar username={m.username} size="lg" level={m.level} />
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold text-white">{m.username}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-cap-text-secondary text-xs">Niveau {m.level} — {lvl.name}</span>
                {(m.badges || []).length > 0 && <BadgePill badgeId={m.badges[0]} />}
              </div>
              <p className="text-cap-text-muted text-xs italic mt-1">{m.style}</p>
            </div>
            <div className="text-right">
              <p className={`font-display text-2xl font-bold ${(m.ytdPerf || 0) >= 0 ? 'text-[#00e5a0]' : 'text-red-400'}`}>{(m.ytdPerf || 0) >= 0 ? '+' : ''}{m.ytdPerf || 0}%</p>
              <p className="text-cap-text-muted text-xs">YTD</p>
            </div>
          </div>
          {m.totalValue > 0 && <p className="text-cap-text-muted text-xs mb-2">Valeur totale : {fmtCur(m.totalValue)}</p>}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="bg-[#1a1d27] border border-white/5 rounded-xl p-6 lg:col-span-4">
            <h3 className="font-display text-sm font-semibold text-white mb-4">Répartition</h3>
            {allocData.length > 0 && (
              <div className="w-36 h-36 mx-auto">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart><Pie data={allocData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} dataKey="value">
                    {allocData.map(e => <Cell key={e.name} fill={ALLOC_COLORS[e.name] || '#94a3b8'} />)}
                  </Pie><Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} formatter={(v) => fmtCur(v)} /></PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="space-y-2 mt-4">
              {allocData.map(a => (
                <div key={a.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ALLOC_COLORS[a.name] || '#94a3b8' }} /><span className="text-cap-text-secondary">{a.name}</span></div>
                  <span className="text-white font-mono">{fmtCur(a.value)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#1a1d27] border border-white/5 rounded-xl overflow-hidden lg:col-span-8">
            <table className="w-full text-left">
              <thead><tr className="border-b border-white/5 text-cap-text-muted text-xs uppercase">
                <th className="py-3 px-4">Ticker</th><th className="py-3 px-4">Nom</th><th className="py-3 px-4">Type</th><th className="py-3 px-4 text-right">Valeur</th><th className="py-3 px-4 text-right">%</th>
              </tr></thead>
              <tbody>
                {(m.allPositions || []).map((p, i) => (
                  <tr key={p.ticker + i} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}>
                    <td className="py-2.5 px-4 font-mono text-[#00e5a0] font-bold text-sm">{p.ticker}</td>
                    <td className="py-2.5 px-4 text-white text-sm">{p.name}</td>
                    <td className="py-2.5 px-4 text-cap-text-secondary text-xs">{p.type}</td>
                    <td className="py-2.5 px-4 text-right text-cap-text-secondary text-sm font-mono">{fmtCur(p.value)}</td>
                    <td className="py-2.5 px-4 text-right text-white text-sm font-bold">{p.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="portfolios-tab">
      <div className="flex items-center justify-between">
        <p className="text-cap-text-secondary text-sm">Membres ayant activé le mode "Portefeuille transparent"</p>
        <button onClick={() => setShowSharing(true)} className="bg-white/5 border border-white/10 text-white text-xs h-8 px-4 rounded-lg flex items-center gap-1.5 hover:bg-white/10 transition-colors" data-testid="config-sharing-btn">
          <Settings className="w-3.5 h-3.5" /> Configurer mon partage
        </button>
      </div>
      {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cap-text-muted" /></div>}
      {!loading && members.length === 0 && (
        <div className="bg-[#1a1d27] border border-white/5 rounded-xl p-8 text-center">
          <p className="text-cap-text-muted text-sm">Aucun membre n'a encore partagé son portefeuille.</p>
          <p className="text-cap-text-muted text-xs mt-2">Activez votre partage pour être le premier !</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map((m, i) => {
          const lvl = getLevel(m.xp || 0);
          return (
            <motion.div key={m.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="bg-[#1a1d27] border border-white/5 rounded-xl p-5 hover:border-[#00e5a0]/30 transition-all cursor-pointer"
              onClick={() => setSelected(m)} data-testid={`portfolio-card-${m.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <MemberAvatar username={m.username} level={m.level} />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{m.username}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-cap-text-muted text-[10px]">Niv. {m.level} {lvl.name}</span>
                    {(m.badges || []).length > 0 && <BadgePill badgeId={m.badges[0]} />}
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-display text-lg font-bold ${(m.ytdPerf || 0) >= 0 ? 'text-[#00e5a0]' : 'text-red-400'}`}>{(m.ytdPerf || 0) >= 0 ? '+' : ''}{m.ytdPerf || 0}%</p>
                  <p className="text-cap-text-muted text-[10px]">YTD</p>
                </div>
              </div>
              <p className="text-cap-text-muted text-[10px] italic mb-2">Style : {m.style}</p>
              <div className="flex items-center gap-2 mb-3">
                {(m.positions || []).slice(0, 3).map((p, j) => (
                  <span key={p.ticker + j} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-cap-text-secondary">{p.ticker} <span className="text-white">{p.pct}%</span></span>
                ))}
              </div>
              <div className="w-full text-[#00e5a0] text-xs h-8 flex items-center justify-center gap-1 bg-[#00e5a0]/5 rounded-lg hover:bg-[#00e5a0]/10 transition-colors">
                Voir le portefeuille <ArrowRight className="w-3 h-3" />
              </div>
            </motion.div>
          );
        })}
      </div>
      <SharingSettingsModal open={showSharing} onClose={() => setShowSharing(false)} />
    </div>
  );
};

// ══════════════════════════════════════
// TENDANCES TAB (API-backed, 5 blocks)
// ══════════════════════════════════════
const TendancesTab = () => {
  const [data, setData] = useState({ topHeld: [], distribution: [], topBought: [], topSold: [], topTheses: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    communityApi.getTrends()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const StockCard = ({ item, idx }) => (
    <div className="bg-[#1a1d27] border border-white/5 rounded-xl p-4 flex items-center justify-between" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>
      <div className="flex items-center gap-3">
        <span className="text-cap-text-muted text-xs font-mono w-5">#{idx + 1}</span>
        <div>
          <span className="font-mono font-bold text-[#00e5a0] text-sm">{item.ticker}</span>
          <p className="text-cap-text-muted text-[10px]">{item.name}</p>
        </div>
      </div>
      <span className="text-cap-text-secondary text-xs">{item.count} membre{item.count > 1 ? 's' : ''}</span>
    </div>
  );

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cap-text-muted" /></div>;

  const hasData = data.topHeld.length > 0 || data.distribution.length > 0;
  if (!hasData) return (
    <div className="bg-[#1a1d27] border border-white/5 rounded-xl p-8 text-center" data-testid="tendances-tab">
      <p className="text-cap-text-muted text-sm">Pas encore de tendances disponibles.</p>
      <p className="text-cap-text-muted text-xs mt-2">Les tendances apparaîtront lorsque les membres partageront leurs portefeuilles.</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="tendances-tab">
      {/* Row 1: 2 columns — Block 1 + Block 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Block 1: Assets les plus possédés */}
        <div className="bg-[#141720] border border-white/5 rounded-xl p-5">
          <h3 className="font-display text-sm font-semibold text-white mb-4">Assets les plus possédés</h3>
          {data.topHeld.length === 0 ? (
            <p className="text-cap-text-muted text-xs py-4 text-center">Pas encore de données</p>
          ) : (
            <div className="space-y-2.5">
              {data.topHeld.map((h, i) => (
                <div key={h.ticker} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-3">
                    <span className="text-cap-text-muted text-xs font-mono w-5">#{i + 1}</span>
                    <span className="font-mono font-bold text-[#00e5a0] text-sm">{h.ticker}</span>
                    <span className="text-cap-text-secondary text-xs">{h.name}</span>
                  </div>
                  <span className="text-cap-text-muted text-xs">{h.count} membre{h.count > 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Block 2: Répartition de la communauté */}
        <div className="bg-[#141720] border border-white/5 rounded-xl p-5">
          <h3 className="font-display text-sm font-semibold text-white mb-4">Répartition de la communauté</h3>
          {data.distribution.length === 0 ? (
            <p className="text-cap-text-muted text-xs py-4 text-center">Pas encore de données</p>
          ) : (
            <div className="space-y-4">
              {data.distribution.map(s => (
                <div key={s.name}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-cap-text-secondary">{s.name}</span>
                    <span className="text-white font-bold">{s.pct}%</span>
                  </div>
                  <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${s.pct}%` }} transition={{ duration: 0.6, delay: 0.1 }}
                      className="h-full rounded-full" style={{ backgroundColor: s.color }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: 2 columns — Block 3 + Block 4 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Block 3: Plus achetés ce mois */}
        <div>
          <h3 className="font-display text-sm font-semibold text-white mb-3">Plus achetés ce mois</h3>
          {data.topBought.length === 0 ? (
            <div className="bg-[#1a1d27] border border-white/5 rounded-xl p-4 text-center">
              <p className="text-cap-text-muted text-xs">Aucun achat ce mois</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.topBought.map((h, i) => <StockCard key={h.ticker} item={h} idx={i} />)}
            </div>
          )}
        </div>
        {/* Block 4: Plus vendus ce mois */}
        <div>
          <h3 className="font-display text-sm font-semibold text-white mb-3">Plus vendus ce mois</h3>
          {data.topSold.length === 0 ? (
            <div className="bg-[#1a1d27] border border-white/5 rounded-xl p-4 text-center">
              <p className="text-cap-text-muted text-xs">Aucune vente ce mois</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.topSold.map((h, i) => <StockCard key={h.ticker} item={h} idx={i} />)}
            </div>
          )}
        </div>
      </div>

      {/* Block 5: Thèses les plus likées ce mois */}
      <div className="bg-[#141720] border border-white/5 rounded-xl p-5">
        <h3 className="font-display text-sm font-semibold text-white mb-4">Thèses les plus likées ce mois</h3>
        {(data.topTheses || []).length === 0 ? (
          <p className="text-cap-text-muted text-xs py-4 text-center">Aucune thèse likée ce mois</p>
        ) : (
          <div className="space-y-3">
            {(data.topTheses || []).map((t, i) => (
              <div key={t.title + i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
                <div className="flex items-center gap-3">
                  <span className="text-cap-text-muted text-xs font-mono w-5">#{i + 1}</span>
                  <div>
                    <p className="text-white text-sm font-medium">{t.title}</p>
                    <p className="text-cap-text-muted text-[10px]">par {t.author}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-red-400">
                  <Heart className="w-3.5 h-3.5 fill-current" />
                  <span className="text-xs font-bold">{t.likes}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════
// CLASSEMENT TAB (API-backed, monthly vs alltime)
// ══════════════════════════════════════
const ClassementTab = ({ currentUsername }) => {
  const [sub, setSub] = useState('xp');
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const medalIcons = ['🥇', '🥈', '🥉'];

  useEffect(() => {
    setLoading(true);
    communityApi.getLeaderboard(sub, period).then(setData).catch(() => setData([])).finally(() => setLoading(false));
  }, [sub, period]);

  return (
    <div className="space-y-4" data-testid="classement-tab">
      {/* Lots du mois */}
      <div className="bg-gradient-to-r from-amber-900/30 via-amber-800/20 to-amber-900/30 border border-amber-500/20 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3"><Gift className="w-5 h-5 text-amber-400" /><h3 className="font-display text-sm font-bold text-amber-300">Lots du mois</h3></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="bg-black/20 rounded-xl p-3"><span className="text-amber-400 font-bold">🥇 Top 1</span><p className="text-white mt-1">Action surprise + Coaching 1:1</p></div>
          <div className="bg-black/20 rounded-xl p-3"><span className="text-gray-300 font-bold">🥈 Top 2-3</span><p className="text-white mt-1">Carte cadeau Amazon</p></div>
          <div className="bg-black/20 rounded-xl p-3"><span className="text-orange-400 font-bold">🥉 Top 4-10</span><p className="text-white mt-1">Badge exclusif + mise en avant profil</p></div>
        </div>
        <p className="text-amber-400/60 text-[10px] mt-3 italic">La roue des lots sera révélée le 1er de chaque mois</p>
      </div>
      {/* Sub-tabs */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          {[{ key: 'xp', label: 'Top XP' }, { key: 'perf', label: 'Top Performance' }].map(s => (
            <button key={s.key} onClick={() => setSub(s.key)}
              className={`text-xs px-4 py-2 rounded-lg transition-all ${sub === s.key ? 'bg-[#00e5a0]/20 text-[#00e5a0] font-bold' : 'bg-white/5 text-cap-text-secondary hover:bg-white/10'}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="h-4 w-px bg-white/10" />
        <div className="flex items-center gap-2">
          {[{ key: 'month', label: 'Ce mois' }, { key: 'alltime', label: 'All-time' }].map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-all ${period === p.key ? 'bg-white/10 text-white font-semibold' : 'bg-white/[0.02] text-cap-text-muted hover:bg-white/5'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {/* Table */}
      <div className="bg-[#1a1d27] border border-white/5 rounded-xl overflow-hidden">
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cap-text-muted" /></div> : data.length === 0 ? (
          <p className="text-cap-text-muted text-sm text-center py-8">Aucune donnée pour cette période.</p>
        ) : (
          <table className="w-full text-left">
            <thead><tr className="border-b border-white/5 text-cap-text-muted text-xs uppercase">
              <th className="py-3 px-4 w-14">#</th><th className="py-3 px-4">Membre</th><th className="py-3 px-4">Niveau</th>
              <th className="py-3 px-4 text-right">{sub === 'xp' ? 'XP' : 'Performance'}</th>
              {sub === 'perf' && <th className="py-3 px-4 text-right">Statut</th>}
            </tr></thead>
            <tbody>
              {data.map((u, i) => {
                const isMe = u.username === currentUsername;
                const isTop3 = i < 3;
                return (
                  <tr key={u.rank + u.username} className={`border-b border-white/5 transition-colors ${isMe ? 'bg-[#00e5a0]/5 border-l-2 border-l-[#00e5a0]' : isTop3 ? 'bg-white/[0.02]' : ''}`}
                    data-testid={`rank-${u.rank}`}>
                    <td className="py-3 px-4">{isTop3 ? <span className="text-lg">{medalIcons[i]}</span> : <span className="text-cap-text-muted font-mono text-sm">{u.rank}</span>}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <MemberAvatar username={u.username} size="sm" />
                        <div>
                          <p className={`text-sm font-semibold ${isMe ? 'text-[#00e5a0]' : 'text-white'}`}>{u.username}{isMe && ' (vous)'}</p>
                          {(u.badges || []).length > 0 && <BadgePill badgeId={u.badges[0]} />}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cap-text-secondary text-xs">Niv. {u.level}</td>
                    <td className="py-3 px-4 text-right">
                      {sub === 'xp' ? <span className="text-cap-text-secondary text-sm font-mono">{(u.xp || 0).toLocaleString()}</span>
                        : <span className={`font-display font-bold text-sm ${(u.perf || '').startsWith('+') ? 'text-[#00e5a0]' : 'text-red-400'}`}>{u.perf}</span>}
                    </td>
                    {sub === 'perf' && (
                      <td className="py-3 px-4 text-right">
                        {u.verification_status === 'verified' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Vérifié</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════
// BADGES TAB
// ══════════════════════════════════════
const BadgesTab = ({ unlockedBadges }) => (
  <div className="space-y-4" data-testid="badges-tab">
    <p className="text-cap-text-secondary text-sm">Collectionnez les badges en accomplissant des défis</p>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {BADGES.map(b => {
        const unlocked = unlockedBadges.includes(b.id);
        return (
          <div key={b.id} className={`bg-[#1a1d27] border rounded-xl p-5 text-center transition-all ${unlocked ? 'border-[#00e5a0]/30 hover:border-[#00e5a0]/50' : 'border-white/5 opacity-50'}`}
            data-testid={`badge-${b.id}`}>
            <div className={`text-3xl mb-3 ${unlocked ? '' : 'grayscale'}`}>{b.icon}</div>
            <p className={`font-display text-sm font-bold mb-1 ${unlocked ? 'text-white' : 'text-cap-text-muted'}`}>{b.name}</p>
            <p className="text-cap-text-muted text-[10px] mb-2">{b.desc}</p>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${unlocked ? 'bg-[#00e5a0]/15 text-[#00e5a0]' : 'bg-white/5 text-cap-text-muted'}`}>+{b.xp} XP</span>
          </div>
        );
      })}
    </div>
  </div>
);

// ══════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════
const CommunityPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('Fil');
  const [showThesisForm, setShowThesisForm] = useState(false);
  const [xpData, setXpData] = useState({ xp: 0, level: 1 });
  const [thesesRefresh, setThesesRefresh] = useState(0);
  const [myRank, setMyRank] = useState('—');
  const [unlockedBadges, setUnlockedBadges] = useState([]);

  const userId = user?.id;
  const isAdmin = user?.username === 'wazzl';

  useEffect(() => {
    if (!userId) return;
    communityApi.getXp().then(setXpData).catch(() => {});
    // Fetch user rank from alltime leaderboard
    communityApi.getLeaderboard('xp', 'alltime').then(data => {
      const idx = data.findIndex(u => u.username === user?.username);
      setMyRank(idx >= 0 ? idx + 1 : '—');
    }).catch(() => {});
    // Fetch user badges from API
    communityApi.getBadges().then(res => {
      setUnlockedBadges(res.badges || []);
    }).catch(() => {});
  }, [userId, user?.username, thesesRefresh]);

  const xp = xpData.xp || user?.xp || 0;
  const lvl = getLevel(xp);
  const next = getNextLevel(xp);
  const pctToNext = next ? Math.min(((xp - lvl.minXp) / (next.minXp - lvl.minXp)) * 100, 100) : 100;
  const xpToNext = next ? next.minXp - xp : 0;

  const handleThesisSubmit = useCallback(async (data) => {
    try {
      await communityApi.createThesis(data);
      setShowThesisForm(false);
      setThesesRefresh(r => r + 1);
      toast.success('+30 XP — Thèse publiée !', { description: `"${data.title}" est maintenant visible.`, duration: 4000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
      throw err;
    }
  }, []);

  const handleXpChange = useCallback((amount) => {
    setXpData(prev => ({ ...prev, xp: (prev.xp || 0) + amount }));
  }, []);

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="community-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-white mb-1">Communauté</h1>
          <p className="text-cap-text-secondary text-sm">Partagez, apprenez et progressez avec les autres membres</p>
        </div>
        <NotificationsPanel />
      </div>

      {/* Stats Banner */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#141720] border border-white/5 rounded-xl p-6 mb-6">
        <div className="grid grid-cols-3 gap-6 mb-4">
          <div className="text-center">
            <div className="w-11 h-11 rounded-xl bg-[#00e5a0]/10 flex items-center justify-center mx-auto mb-2"><Award className="w-5 h-5 text-[#00e5a0]" /></div>
            <p className="font-display text-2xl font-bold text-white" data-testid="user-level">{lvl.level}</p>
            <p className="text-cap-text-secondary text-xs">{lvl.name}</p>
          </div>
          <div className="text-center">
            <div className="w-11 h-11 rounded-xl bg-[#4f8eff]/10 flex items-center justify-center mx-auto mb-2"><Star className="w-5 h-5 text-[#4f8eff]" /></div>
            <p className="font-display text-2xl font-bold text-white" data-testid="user-xp">{xp.toLocaleString()}</p>
            <p className="text-cap-text-secondary text-xs">Points XP</p>
          </div>
          <div className="text-center">
            <div className="w-11 h-11 rounded-xl bg-[#7c3aed]/10 flex items-center justify-center mx-auto mb-2"><Trophy className="w-5 h-5 text-[#7c3aed]" /></div>
            <p className="font-display text-2xl font-bold text-white">#{myRank}</p>
            <p className="text-cap-text-secondary text-xs">Classement</p>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-cap-text-secondary text-xs">Niveau {lvl.level} — {lvl.name}</span>
            {next && <span className="text-cap-text-muted text-[10px]">{xpToNext.toLocaleString()} XP pour atteindre {next.name}</span>}
          </div>
          <Progress value={pctToNext} className="h-2.5 bg-white/5" />
        </div>
      </motion.div>

      {/* Tab navigation - 8 tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-6 scrollbar-hide">
        {TAB_LIST.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase().replace('&', 'a')}`}
            className={`text-sm px-4 py-2 rounded-lg whitespace-nowrap transition-all ${tab === t ? 'bg-[#00e5a0]/15 text-[#00e5a0] font-bold' : 'bg-white/5 text-cap-text-secondary hover:bg-white/10'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div key={tab + thesesRefresh} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
          {tab === 'Fil' && <FeedTab />}
          {tab === 'Thèses' && <ThesesTab onOpenForm={() => setShowThesisForm(true)} currentUsername={user?.username} onXpChange={handleXpChange} />}
          {tab === 'Q&A' && <QATab onXpChange={handleXpChange} />}
          {tab === 'Résultats' && <ResultsTab username={user?.username} isAdmin={isAdmin} onXpChange={handleXpChange} />}
          {tab === 'Portefeuilles' && <PortfoliosTab />}
          {tab === 'Tendances' && <TendancesTab />}
          {tab === 'Classement' && <ClassementTab currentUsername={user?.username} />}
          {tab === 'Badges' && <BadgesTab unlockedBadges={unlockedBadges} />}
        </motion.div>
      </AnimatePresence>

      <ThesisFormModal open={showThesisForm} onClose={() => setShowThesisForm(false)} onSubmit={handleThesisSubmit} userId={userId} username={user?.username} />
    </div>
  );
};

export default CommunityPage;
